import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Tooltip, IconButton, Menu, MenuItem, Divider, Badge, Popper, Paper, List, ListItem, ListItemText, ListItemAvatar, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, InputAdornment } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { API_BASE_URL } from '../config';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CategoryIcon from '@mui/icons-material/Category';
import ImageIcon from '@mui/icons-material/Image';
import ListIcon from '@mui/icons-material/List';
import TemplateIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaletteIcon from '@mui/icons-material/Palette';
import StyleIcon from '@mui/icons-material/Style';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LanguageIcon from '@mui/icons-material/Language';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useAiCommentBadge } from '../modules/aiImageStudio/hooks/useAiCommentBadge';
import CampaignIcon from '@mui/icons-material/Campaign';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { IUser } from '../types/damTypes';
import UploadCenter from './UploadCenter';
import { changePassword } from '../services/authService';
import { hasPermission, canAccessProductComparison } from '../utils/permissionUtils';
import TabletTopBarControls from '../tablet/TabletTopBarControls';

interface TopAppBarProps {
  onUploadClick: () => void;
  onEnhancedUploadClick?: () => void;
  onAddCategoryClick: () => void;
  onManageCategoriesClick: () => void;
  onRegenerateThumbnailsClick: () => void;
  onManageTemplatesClick?: () => void;
  onManageBadgesClick?: () => void;
  onOpenPopupSettings?: () => void;
  onOpenInventorySettings?: () => void;
  onOpenCsvSettings?: () => void;
  onOpenFieldDiagnostics?: () => void;
  onOpenDownloadTemplates?: () => void; // open Template-builder tab in settings
  onOpenBrandGuide?: () => void;
  onOpenPptPolicies?: () => void;
  onOpenPresentations?: () => void;
  onOpenTrends?: () => void;
  onOpenBriefs?: () => void;
  onOpenPrompts?: () => void;
  onOpenAiImageStudio?: () => void;
  onOpenListningar?: () => void;
  onOpenNyhetslanseringar?: () => void;
  onOpenInhouse?: () => void;
  onOpenBarcodeGenerator?: () => void;
  multiSelectEnabled?: boolean;
  selectedAssetCount?: number;
  onToggleMultiSelect?: () => void;
  onOpenRoleSettings?: () => void;
  onOpenUserSettings?: () => void;
  onOpenApiKeySettings?: () => void;
  onOpenBackupSettings?: () => void;
  onOpenArchive?: () => void;
  onOpenDuplicates?: () => void;
  onOpenIssuesTasks?: () => void;
  onReportBug?: () => void;
  onLogoClick?: () => void; // Navigate to main view (close settings)
  onOpenSettings?: () => void; // Open settings directly (remember last tab)
  onOpenProductComparison?: () => void;
  onOpenWordPressPublishing?: () => void;
  wordpressPendingAssetCount?: number;
  onSyncAllDabas?: () => void; // Sync all assets with Dabas
  cartItems?: {[assetId: string]: number};
  onCartClick?: () => void;
  cartAssets?: any[]; // For showing product names in hover
  currentUser: IUser | null;
  onLogout: (options?: { reason?: string; server?: boolean }) => void;
  // Landscape mobile compact mode
  compactLandscape?: boolean;
  landscapeSearchTerm?: string;
  onLandscapeSearchChange?: (value: string) => void;
  onOpenCategoryDrawer?: () => void;
  onToggleLandscapeCompact?: () => void;
  tabletShellMode?: boolean;
  tabletSearchTerm?: string;
  onTabletSearchChange?: (value: string) => void;
  onOpenTabletCategoryDrawer?: () => void;
  onOpenTabletFilters?: () => void;
}

const NAV_LABEL_SAFETY_MARGIN_PX = 16;
const NAV_LABEL_RESTORE_BUFFER_PX = 24;

function TopAppBar({ 
  onUploadClick, 
  onEnhancedUploadClick,
  onAddCategoryClick, 
  onManageCategoriesClick,
  onRegenerateThumbnailsClick,
  onManageTemplatesClick,
  onManageBadgesClick,
  onOpenPopupSettings,
  onOpenInventorySettings,
  onOpenCsvSettings,
  onOpenFieldDiagnostics,
  onOpenDownloadTemplates,
  onOpenBrandGuide,
  onOpenPptPolicies,
  onOpenPresentations,
  onOpenTrends,
  onOpenBriefs,
  onOpenPrompts,
  onOpenAiImageStudio,
  onOpenListningar,
  onOpenNyhetslanseringar,
  onOpenInhouse,
  onOpenBarcodeGenerator,
  multiSelectEnabled = false,
  selectedAssetCount = 0,
  onToggleMultiSelect,
  onOpenRoleSettings,
  onOpenUserSettings,
  onOpenApiKeySettings,
  onOpenBackupSettings,
  onOpenArchive,
  onOpenDuplicates,
  onOpenIssuesTasks,
  onReportBug,
  onLogoClick,
  onOpenSettings,
  onOpenProductComparison,
  onOpenWordPressPublishing,
  wordpressPendingAssetCount = 0,
  onSyncAllDabas,
  cartItems = {},
  onCartClick,
  cartAssets,
  currentUser,
  onLogout,
  compactLandscape = false,
  landscapeSearchTerm = '',
  onLandscapeSearchChange,
  onOpenCategoryDrawer,
  onToggleLandscapeCompact,
  tabletShellMode = false,
  tabletSearchTerm = '',
  onTabletSearchChange,
  onOpenTabletCategoryDrawer,
  onOpenTabletFilters,
}: TopAppBarProps) {
  const [uploadMenuAnchor, setUploadMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [cartHoverAnchor, setCartHoverAnchor] = React.useState<null | HTMLElement>(null);
  const [showCartHover, setShowCartHover] = React.useState(false);
  const cartHoverTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const showCartHoverWithDelay = () => {
    if (cartHoverTimeout.current) { clearTimeout(cartHoverTimeout.current); cartHoverTimeout.current = null; }
    setShowCartHover(true);
  };
  const hideCartHoverWithDelay = () => {
    if (cartHoverTimeout.current) clearTimeout(cartHoverTimeout.current);
    cartHoverTimeout.current = setTimeout(() => setShowCartHover(false), 200);
  };
  const [openChangePassword, setOpenChangePassword] = React.useState(false);
  const [cpCurrent, setCpCurrent] = React.useState('');
  const [cpNew, setCpNew] = React.useState('');
  const [cpConfirm, setCpConfirm] = React.useState('');
  const [cpError, setCpError] = React.useState<string | null>(null);
  const [cpLoading, setCpLoading] = React.useState(false);
  const [confirmSyncDabas, setConfirmSyncDabas] = React.useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = React.useState(false);
  const [logoVersion] = React.useState(() => Date.now());
  const [hideNavLabels, setHideNavLabels] = React.useState(false);
  const logoContainerRef = React.useRef<HTMLDivElement | null>(null);
  const navAreaRef = React.useRef<HTMLDivElement | null>(null);
  const navItemsRef = React.useRef<HTMLDivElement | null>(null);
  const expandedNavWidthRef = React.useRef<number | null>(null);
  const canAccessIssuesTasks = hasPermission(currentUser, 'issues:read') || hasPermission(currentUser, 'tasks:read');
  const canManageProductComparison = canAccessProductComparison(currentUser);
  const canOpenSettings =
    hasPermission(currentUser, 'categories:read') ||
    hasPermission(currentUser, 'categories:create') ||
    hasPermission(currentUser, 'templates:read') ||
    hasPermission(currentUser, 'downloadTemplates:read') ||
    hasPermission(currentUser, 'assets:update') ||
    hasPermission(currentUser, 'system:manageSettings') ||
    hasPermission(currentUser, 'system:manageMaintenance') ||
    hasPermission(currentUser, 'system:manageInventory') ||
    hasPermission(currentUser, 'roles:read') ||
    hasPermission(currentUser, 'users:read') ||
    canAccessIssuesTasks ||
    canManageProductComparison;
  const canReportBug = hasPermission(currentUser, 'issues:create');
  const showTabletShellControls = tabletShellMode && !compactLandscape;
  const aiCommentBadgeCount = useAiCommentBadge();
  const safeWordPressPendingAssetCount = Math.max(0, Number(wordpressPendingAssetCount) || 0);
  const canOpenWordPressPublishingShortcut = hasPermission(currentUser, 'admin:all') && !!onOpenWordPressPublishing;

  // Responsive nav button styles
  const navButtonSx = {
    flexDirection: { xs: 'row', md: 'column' },
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: 'auto', md: 48 },
    px: { xs: 1, md: 0.5 },
    py: { xs: 0.5, md: 0.5 },
    gap: { md: 0.25 },
    '& .MuiButton-startIcon': {
      margin: { xs: '0 4px 0 0', md: 0 }
    }
  };

  const navTextSx = {
    display: hideNavLabels ? 'none' : { xs: 'none', md: 'block' },
    fontSize: { md: '0.6rem', lg: '0.6rem' },
    lineHeight: { md: 1.2, lg: 1.2 },
    whiteSpace: 'nowrap',
    textAlign: 'center'
  };
  
  React.useLayoutEffect(() => {
    if (compactLandscape || showTabletShellControls) {
      expandedNavWidthRef.current = null;
      setHideNavLabels(false);
      return;
    }

    const logoContainer = logoContainerRef.current;
    const navArea = navAreaRef.current;
    const navItems = navItemsRef.current;
    if (!logoContainer || !navArea || !navItems) return;

    let animationFrame = 0;

    const measure = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const availableWidth = navArea.getBoundingClientRect().width;

        if (!hideNavLabels) {
          const expandedWidth = navItems.getBoundingClientRect().width;
          expandedNavWidthRef.current = expandedWidth;
          setHideNavLabels(expandedWidth + NAV_LABEL_SAFETY_MARGIN_PX > availableWidth);
          return;
        }

        const expandedWidth = expandedNavWidthRef.current;
        if (
          expandedWidth !== null &&
          availableWidth >= expandedWidth + NAV_LABEL_RESTORE_BUFFER_PX
        ) {
          setHideNavLabels(false);
        }
      });
    };

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(logoContainer);
    resizeObserver.observe(navArea);
    resizeObserver.observe(navItems);

    const contentObserver = new MutationObserver(() => {
      expandedNavWidthRef.current = null;
      setHideNavLabels(false);
    });
    contentObserver.observe(navItems, { childList: true });

    measure();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      contentObserver.disconnect();
    };
  }, [compactLandscape, hideNavLabels, showTabletShellControls]);
  
  const handleUploadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUploadMenuAnchor(event.currentTarget);
  };
  
  const handleUploadMenuClose = () => {
    setUploadMenuAnchor(null);
  };
  
  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };
  
  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const openSettingsShortcut = (view: 'issues-tasks' | 'pdf-templates' | 'wordpress-publishing' | 'maintenance' | 'pptx-template-field-import') => {
    handleSettingsMenuClose();
    try {
      window.location.hash = view;
    } catch {}
    if (onOpenSettings) onOpenSettings();
  };
  
  const handleBasicUploadClick = () => {
    handleUploadMenuClose();
    onUploadClick();
  };
  
  const handleEnhancedUploadClick = () => {
    handleUploadMenuClose();
    if (onEnhancedUploadClick) onEnhancedUploadClick();
  };
  
  const handleManageTemplatesClick = () => {
    handleSettingsMenuClose();
    if (onManageTemplatesClick) onManageTemplatesClick();
  };

  const handleManageBadgesClick = () => {
    handleSettingsMenuClose();
    if (onManageBadgesClick) onManageBadgesClick();
  };

  const handlePopupSettingsClick = () => {
    handleSettingsMenuClose();
    if (onOpenPopupSettings) onOpenPopupSettings();
  };

  const handleAddCategoryClick = () => {
    handleSettingsMenuClose();
    onAddCategoryClick();
  };

  const handleManageCategoriesClick = () => {
    handleSettingsMenuClose();
    onManageCategoriesClick();
  };

  const handleOpenRoles = () => {
    handleSettingsMenuClose();
    if (onOpenRoleSettings) onOpenRoleSettings();
  };

  const handleOpenUsers = () => {
    handleSettingsMenuClose();
    if (onOpenUserSettings) onOpenUserSettings();
  };

  const handleOpenInventory = () => {
    handleSettingsMenuClose();
    if (onOpenInventorySettings) onOpenInventorySettings();
  };

  const handleOpenCsv = () => {
    handleSettingsMenuClose();
    if (onOpenCsvSettings) onOpenCsvSettings();
  };

  const handleOpenProductComparison = () => {
    handleSettingsMenuClose();
    if (onOpenProductComparison) onOpenProductComparison();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    onLogout();
  };

  const handleOpenChangePassword = () => {
    handleUserMenuClose();
    setCpCurrent('');
    setCpNew('');
    setCpConfirm('');
    setCpError(null);
    setOpenChangePassword(true);
  };

  const handleConfirmChangePassword = async () => {
    try {
      setCpError(null);
      if (!cpCurrent || !cpNew) {
        setCpError('Fyll i alla fält.');
        return;
      }
      if (cpNew !== cpConfirm) {
        setCpError('Det nya lösenordet och bekräftelsen matchar inte.');
        return;
      }
      setCpLoading(true);
      const resp = await changePassword(cpCurrent, cpNew);
      if (resp.error) {
        setCpError(resp.error || 'Kunde inte byta lösenord.');
      } else {
        setOpenChangePassword(false);
        if (resp.data?.logoutRequired) {
          onLogout({ reason: 'password_changed', server: false });
        }
      }
    } catch (err: any) {
      setCpError(err?.message || 'Ett fel inträffade.');
    } finally {
      setCpLoading(false);
    }
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar sx={compactLandscape ? { minHeight: 36, maxHeight: 36, px: 1, py: 0 } : showTabletShellControls ? { minHeight: 64, px: 1.5, gap: 1 } : undefined}>
        {compactLandscape ? (
          /* Landscape mobile: hamburger menu + search instead of logo */
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1, flexGrow: 1, minWidth: 0 }}>
            <IconButton color="inherit" edge="start" onClick={onOpenCategoryDrawer} aria-label="Kategorier" size="small">
              <MenuIcon />
            </IconButton>
            {/* Toggle for landscape compact mode — hidden, controlled via system settings instead
            {onToggleLandscapeCompact && (
              <Tooltip title="Visa standard desktop-layout">
                <IconButton color="inherit" onClick={onToggleLandscapeCompact} size="small">
                  <FullscreenExitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            */}
            <TextField
              value={landscapeSearchTerm}
              onChange={(e) => onLandscapeSearchChange?.(e.target.value)}
              placeholder="Sök…"
              size="small"
              variant="outlined"
              sx={{
                width: 180,
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  height: 28,
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputAdornment-root': { color: 'rgba(255,255,255,0.7)' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  </InputAdornment>
                ),
                endAdornment: landscapeSearchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onLandscapeSearchChange?.('')} edge="end">
                      <ClearIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: showTabletShellControls ? 1.5 : 0, flexGrow: showTabletShellControls ? 1 : 0, flexShrink: 0, minWidth: 0 }}>
            <Box 
              ref={logoContainerRef}
              sx={{ 
                flexShrink: 0,
                cursor: onLogoClick ? 'pointer' : 'default',
                '&:hover': onLogoClick ? { opacity: 0.8 } : {}
              }}
              onClick={onLogoClick}
            >
              {!logoLoadFailed ? (
                <img
                  src={`/logo.png?v=${logoVersion}`}
                  alt="Logo"
                  style={{ height: '40px', display: 'block' }}
                  onError={() => setLogoLoadFailed(true)}
                />
              ) : (
                <Typography variant="h6" component="div">
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Athena DAM</Box>
                </Typography>
              )}
            </Box>
            {showTabletShellControls && (
              <TabletTopBarControls
                searchTerm={tabletSearchTerm}
                onSearchTermChange={(value) => onTabletSearchChange?.(value)}
                onOpenCategoryDrawer={() => onOpenTabletCategoryDrawer?.()}
                onOpenFilters={() => onOpenTabletFilters?.()}
                multiSelectEnabled={multiSelectEnabled}
                selectedAssetCount={selectedAssetCount}
                onToggleMultiSelect={() => onToggleMultiSelect?.()}
              />
            )}
          </Box>
        )}
        <Box
          ref={navAreaRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            ...(showTabletShellControls
              ? { flexShrink: 0 }
              : { flexGrow: 1, minWidth: 0 })
          }}
        >
          <Box
            ref={navItemsRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: showTabletShellControls ? 0.5 : 1,
              flexShrink: 0
            }}
          >
          {/* Right-aligned cluster: utilities + settings + user + cart */}
          {/* Upload Center badge */}
          {!showTabletShellControls && <UploadCenter />}
          {/* Multi-select toggle - HIDDEN (Ctrl/Shift selection works without it)
          {onToggleMultiSelect && (
            <Tooltip title={multiSelectEnabled ? "Exit Multi-select" : "Enable Multi-select"}>
              <Button 
                color="inherit" 
                onClick={onToggleMultiSelect}
                startIcon={multiSelectEnabled ? <ListIcon /> : <ViewModuleIcon />}
                sx={{ 
                  borderRadius: '4px', 
                  backgroundColor: multiSelectEnabled ? 'rgba(255,255,255,0.2)' : 'transparent'
                }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {multiSelectEnabled ? "Exit Multi-select" : "Multi-select"}
                </Box>
              </Button>
            </Tooltip>
          )}
          */}
          {hasPermission(currentUser, 'assets:create') && (
            <Box>
              <Button 
                className="upload-button"
                color="inherit" 
                onClick={onEnhancedUploadClick || handleUploadMenuOpen} 
                endIcon={null}
                startIcon={<FileUploadIcon />}
                sx={{ 
                  ...navButtonSx,
                  backgroundColor: '#1b4776', 
                  borderRadius: 1,
                  px: { xs: 1, md: 1, lg: 2 },
                  '&:hover': { backgroundColor: '#1a5580' }
                }}
              >
                <Box sx={navTextSx}>Ladda upp</Box>
              </Button>
              <Menu
                anchorEl={uploadMenuAnchor}
                open={Boolean(uploadMenuAnchor)}
                onClose={handleUploadMenuClose}
              >
                <MenuItem onClick={handleBasicUploadClick}>
                  Basic Upload
                </MenuItem>
                <MenuItem onClick={handleEnhancedUploadClick}>
                  Enhanced Upload (Template-based)
                </MenuItem>
              </Menu>
            </Box>
          )}

          {!showTabletShellControls && onOpenTrends && (
            <Tooltip title="Athena Trends">
              <Button
                className="trends-button"
                color="inherit"
                onClick={onOpenTrends}
                startIcon={<TrendingUpIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>ATHENA TRENDS</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenBrandGuide && (
            <Tooltip title="Grafisk manual">
              <Button
                className="brand-guide-button"
                color="inherit"
                onClick={onOpenBrandGuide}
                startIcon={<StyleIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Grafisk manual</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenPptPolicies && (
            <Tooltip title="PPT Policys">
              <Button
                className="ppt-policies-button"
                color="inherit"
                onClick={onOpenPptPolicies}
                startIcon={<TemplateIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>PPT Policys</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenPresentations && (
            <Tooltip title="Presentations">
              <Button
                className="presentations-button"
                color="inherit"
                onClick={onOpenPresentations}
                startIcon={<SlideshowIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Presentations</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenBriefs && (
            <Tooltip title="Skapa brief">
              <Button
                className="briefs-button"
                color="inherit"
                onClick={onOpenBriefs}
                startIcon={<TemplateIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Skapa brief</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenPrompts && (
            <Tooltip title="Prompt-station">
              <Button
                className="prompts-button"
                color="inherit"
                onClick={onOpenPrompts}
                startIcon={<AutoAwesomeIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Prompt</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenAiImageStudio && (
            <Tooltip title="Athena AI Studio">
              <Badge
                badgeContent={aiCommentBadgeCount}
                color="error"
                overlap="rectangular"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: 18,
                    height: 18,
                    top: 4,
                    right: 4,
                  },
                }}
              >
                <Button
                  className="ai-image-studio-button"
                  color="inherit"
                  onClick={onOpenAiImageStudio}
                  startIcon={<ImageIcon />}
                  sx={navButtonSx}
                >
                  <Box sx={navTextSx}>Athena AI</Box>
                </Button>
              </Badge>
            </Tooltip>
          )}

          {onOpenListningar && (
            <Tooltip title="Listningar">
              <Button
                className="listningar-button"
                color="inherit"
                onClick={onOpenListningar}
                startIcon={<CampaignIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Listningar</Box>
              </Button>
            </Tooltip>
          )}

          {onOpenNyhetslanseringar && (
            <Tooltip title="Nyhetslanseringar">
              <Button
                className="nyhetslanseringar-button"
                color="inherit"
                onClick={onOpenNyhetslanseringar}
                startIcon={<RocketLaunchIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Nyhetslanseringar</Box>
              </Button>
            </Tooltip>
          )}

          {onOpenInhouse && (
            <Tooltip title="Inhouse - Se vad vi gör">
              <Button
                className="inhouse-button"
                color="inherit"
                onClick={onOpenInhouse}
                startIcon={<CampaignIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Inhouse</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenBarcodeGenerator && (
            <Tooltip title="EAN-kodsgenerator">
              <Button
                className="barcode-button"
                color="inherit"
                onClick={onOpenBarcodeGenerator}
                startIcon={<StorefrontIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>EAN-koder</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && onOpenInventorySettings && (
            <Tooltip title="Order">
              <Button
                className="order-button"
                color="inherit"
                onClick={onOpenInventorySettings}
                startIcon={<ReceiptIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Order</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && canManageProductComparison && onOpenProductComparison && (
            <Tooltip title="Produktkontroll">
              <Button
                className="product-comparison-button"
                color="inherit"
                onClick={onOpenProductComparison}
                startIcon={<CompareArrowsIcon />}
                sx={navButtonSx}
              >
                <Box sx={navTextSx}>Produktkontroll</Box>
              </Button>
            </Tooltip>
          )}

          {!showTabletShellControls && canOpenWordPressPublishingShortcut && (
            <Tooltip
              title={safeWordPressPendingAssetCount > 0
                ? `${safeWordPressPendingAssetCount} assets behöver uppmärksammas i WordPress Publishing`
                : 'WordPress Publishing'}
            >
              <Badge
                badgeContent={safeWordPressPendingAssetCount}
                color="error"
                overlap="rectangular"
                invisible={safeWordPressPendingAssetCount <= 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: 18,
                    height: 18,
                    top: 4,
                    right: 4,
                  },
                }}
              >
                <Button
                  className="wordpress-publishing-button"
                  color="inherit"
                  onClick={onOpenWordPressPublishing}
                  startIcon={<LanguageIcon />}
                  sx={navButtonSx}
                >
                  <Box sx={navTextSx}>WordPress</Box>
                </Button>
              </Badge>
            </Tooltip>
          )}

          {/* Settings - show icon button for direct access and dropdown menu */}
          {!showTabletShellControls && canOpenSettings ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Settings dropdown menu with icon */}
              <Tooltip title="Inställningar">
                <Button 
                  className="settings-button"
                  color="inherit" 
                  onClick={handleSettingsMenuOpen}
                  startIcon={<SettingsIcon />}
                  endIcon={null}
                  sx={navButtonSx}
                >
                  <Box sx={navTextSx}>Inställningar</Box>
                </Button>
              </Tooltip>
              <Menu
                anchorEl={settingsMenuAnchor}
                open={Boolean(settingsMenuAnchor)}
                onClose={handleSettingsMenuClose}
              >
                {/* Category Management group */}
                {(hasPermission(currentUser, 'categories:create') || hasPermission(currentUser, 'categories:update')) && (
                  <MenuItem onClick={handleManageCategoriesClick}>
                    <CategoryIcon fontSize="small" sx={{ mr: 1 }} />
                    <ManageSearchIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Hantera kategorier
                  </MenuItem>
                )}
                
                {(hasPermission(currentUser, 'categories:create') || hasPermission(currentUser, 'categories:update')) && 
                  <Divider />
                }
                
                {onManageTemplatesClick && hasPermission(currentUser, 'templates:read') && (
                  <MenuItem onClick={handleManageTemplatesClick}>
                    <TemplateIcon fontSize="small" sx={{ mr: 1 }} />
                    Hantera mallar (Templates)
                  </MenuItem>
                )}
                {hasPermission(currentUser, 'downloadTemplates:read') && (
                  <MenuItem onClick={() => openSettingsShortcut('pdf-templates')}>
                    <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
                    PDF-mallar
                  </MenuItem>
                )}
                
                {onManageBadgesClick && hasPermission(currentUser, 'categories:create') && (
                  <MenuItem onClick={handleManageBadgesClick}>
                    <PaletteIcon fontSize="small" sx={{ mr: 1 }} />
                    Hantera märken (Badges)
                  </MenuItem>
                )}

                {(hasPermission(currentUser, 'templates:read') || hasPermission(currentUser, 'categories:create')) && (
                  <Divider />
                )}
                
                {/* User/Role Management Section */}
                {hasPermission(currentUser, 'roles:read') && (
                  <MenuItem onClick={handleOpenRoles}>
                    <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 1 }} />
                    Hantera roller
                  </MenuItem>
                )}
                
                {hasPermission(currentUser, 'users:read') && (
                  <MenuItem onClick={handleOpenUsers}>
                    <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                    Hantera användare
                  </MenuItem>
                )}
                
                <MenuItem onClick={() => { handleSettingsMenuClose(); if (onOpenArchive) onOpenArchive(); }}>
                  <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
                  Arkiv
                </MenuItem>
                
                {/* Inventory Management Section */}
                {hasPermission(currentUser, 'system:manageInventory') && (
                  <MenuItem onClick={handleOpenInventory}>
                    <StorefrontIcon fontSize="small" sx={{ mr: 1 }} />
                    Lagerhantering och Order
                  </MenuItem>
                )}

                {canManageProductComparison && onOpenProductComparison && (
                  <MenuItem onClick={handleOpenProductComparison}>
                    <CompareArrowsIcon fontSize="small" sx={{ mr: 1 }} />
                    Produktkontroll
                  </MenuItem>
                )}
                {hasPermission(currentUser, 'assets:update') && (
                  <MenuItem onClick={() => openSettingsShortcut('wordpress-publishing')}>
                    <LanguageIcon fontSize="small" sx={{ mr: 1 }} />
                    WordPress Publishing
                  </MenuItem>
                )}
                
                {/* CSV Import/Export Section */}
                {(hasPermission(currentUser, 'csv:import') || hasPermission(currentUser, 'csv:export') || hasPermission(currentUser, 'csv:manageTemplates')) && (
                  <MenuItem onClick={handleOpenCsv}>
                    <ImportExportIcon fontSize="small" sx={{ mr: 1 }} />
                    CSV Import/Export
                  </MenuItem>
                )}
                {hasPermission(currentUser, 'csv:import') && (
                  <MenuItem onClick={() => openSettingsShortcut('pptx-template-field-import')}>
                    <TemplateIcon fontSize="small" sx={{ mr: 1 }} />
                    PowerPoint-import
                  </MenuItem>
                )}
                {canAccessIssuesTasks && (
                  <MenuItem onClick={() => openSettingsShortcut('issues-tasks')}>
                    <ListIcon fontSize="small" sx={{ mr: 1 }} />
                    Buggar & Roadmap
                  </MenuItem>
                )}

                {hasPermission(currentUser, 'system:manageMaintenance') && (
                  <MenuItem onClick={() => openSettingsShortcut('maintenance')}>
                    <CampaignIcon fontSize="small" sx={{ mr: 1 }} />
                    Underhåll
                  </MenuItem>
                )}

                {hasPermission(currentUser, 'assets:update') && onSyncAllDabas && (
                  <MenuItem onClick={() => { handleSettingsMenuClose(); setConfirmSyncDabas(true); }}>
                    <CloudSyncIcon fontSize="small" sx={{ mr: 1, color: '#ff9800' }} />
                    Synka alla med Dabas
                  </MenuItem>
                )}
                
              </Menu>
            </Box>
          ) : null}

          {/* NY: Användarmeny / Logga ut-knapp */}
          {currentUser ? (
            <Box>
              <Tooltip title={currentUser.email || 'Användarinställningar'}>
                <Button 
                  color="inherit" 
                  onClick={handleUserMenuOpen}
                  endIcon={<ArrowDropDownIcon sx={{ display: { xs: 'none', lg: 'inline-flex' } }} />}
                  sx={{ 
                    fontSize: { xs: '0.7rem', md: '0.6rem', lg: '0.75rem' },
                    minWidth: 'auto',
                    px: { xs: 0.5, lg: 1 }
                  }}
                >
                  {currentUser.firstName || currentUser.email}
                </Button>
              </Tooltip>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
              >
                <MenuItem onClick={handleOpenChangePassword}>Byt lösenord</MenuItem>
                <Divider />
                {/* Kan lägga till 'Min Profil' eller liknande här senare */}
                <MenuItem onClick={handleLogoutClick}>Logga ut</MenuItem>
              </Menu>
            </Box>
          ) : (
            // Kan lägga till en 'Logga in'-knapp här om LoginPage inte renderas av App.tsx
            // <Button color="inherit" onClick={() => { /* navigera till login */ }}>Logga in</Button>
            null
          )}

          {/* Shopping Cart Button - Always visible, LAST on the right */}
          {onCartClick && (
            <Box>
              <Tooltip title="Varukorg">
                <IconButton 
                  className="cart-button"
                  color="inherit" 
                  onClick={onCartClick}
                  onMouseEnter={(e) => {
                    setCartHoverAnchor(e.currentTarget);
                    showCartHoverWithDelay();
                  }}
                  onMouseLeave={() => {
                    hideCartHoverWithDelay();
                  }}
                >
                  <Badge 
                    badgeContent={Object.values(cartItems).reduce((sum, count) => sum + count, 0)} 
                    color="error"
                    showZero={false}
                  >
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              {/* Cart Hover Popup */}
              <Popper
                open={showCartHover && Object.keys(cartItems).length > 0}
                anchorEl={cartHoverAnchor}
                placement="bottom-end"
                onMouseEnter={() => showCartHoverWithDelay()}
                onMouseLeave={() => hideCartHoverWithDelay()}
                sx={{ zIndex: 1300 }}
              >
                <Paper 
                  elevation={4} 
                  sx={{ 
                    p: 2, 
                    maxWidth: 300,
                    backgroundColor: 'white',
                    border: '1px solid #ddd'
                  }}
                  onMouseEnter={() => showCartHoverWithDelay()}
                  onMouseLeave={() => hideCartHoverWithDelay()}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Varukorg ({Object.values(cartItems).reduce((sum, count) => sum + count, 0)} artiklar)
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {Object.entries(cartItems).map(([assetId, quantity]) => {
                      const asset = cartAssets?.find(a => a._id === assetId);
                      // Use title if available, otherwise clean filename without extension, otherwise fallback
                      let displayName = '';
                      if (asset?.title && asset.title.trim()) {
                        displayName = asset.title;
                      } else if (asset?.fileName) {
                        // Remove file extension and decode any URL encoding
                        try {
                          displayName = decodeURIComponent(asset.fileName.replace(/\.[^/.]+$/, '').replace(/\+/g, ' '));
                        } catch (e) {
                          displayName = asset.fileName.replace(/\.[^/.]+$/, '');
                        }
                      } else {
                        displayName = `Produkt ${assetId.slice(-6)}`;
                      }
                      
                      // Get thumbnail path - use previewPaths.thumbnail from IAsset
                      const thumbnailPath = asset?.previewPaths?.thumbnail || asset?.previewPaths?.medium;
                      
                      return (
                        <ListItem key={assetId} sx={{ px: 0, py: 0.5 }}>
                          <ListItemAvatar sx={{ minWidth: 44 }}>
                            <Avatar 
                              src={thumbnailPath ? `${API_BASE_URL}${thumbnailPath}` : undefined}
                              variant="rounded"
                              sx={{ width: 36, height: 36, bgcolor: 'grey.200' }}
                            >
                              {!thumbnailPath && <StorefrontIcon sx={{ fontSize: 20, color: 'grey.500' }} />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={displayName}
                            secondary={`Antal: ${quantity}`}
                            primaryTypographyProps={{ fontSize: '0.85rem', noWrap: true }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                  <Typography
                    variant="caption"
                    onClick={() => { setShowCartHover(false); onCartClick(); }}
                    sx={{ mt: 1, display: 'block', fontStyle: 'italic', cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Klicka här för att öppna varukorgen
                  </Typography>
                </Paper>
              </Popper>
            </Box>
          )}
          </Box>
        </Box>
      </Toolbar>
      {/* Dialog: Byt lösenord */}
      <Dialog open={openChangePassword} onClose={() => setOpenChangePassword(false)}>
        <DialogTitle>Byt lösenord</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Ange ditt nuvarande lösenord och ett nytt lösenord.
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="Nuvarande lösenord"
            type="password"
            value={cpCurrent}
            onChange={(e) => setCpCurrent(e.target.value)}
            disabled={cpLoading}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Nytt lösenord"
            type="password"
            value={cpNew}
            onChange={(e) => setCpNew(e.target.value)}
            disabled={cpLoading}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Bekräfta nytt lösenord"
            type="password"
            value={cpConfirm}
            onChange={(e) => setCpConfirm(e.target.value)}
            disabled={cpLoading}
          />
          {cpError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {cpError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChangePassword(false)} disabled={cpLoading}>Avbryt</Button>
          <Button variant="contained" onClick={handleConfirmChangePassword} disabled={cpLoading}>
            {cpLoading ? 'Byter…' : 'Byt lösenord'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Bekräfta synka alla med Dabas */}
      <Dialog open={confirmSyncDabas} onClose={() => setConfirmSyncDabas(false)}>
        <DialogTitle>Synka alla produkter med Dabas?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Är du säker på att du vill synkronisera alla produkter mot Dabas? Detta kan ta en stund beroende på antalet produkter.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSyncDabas(false)}>Avbryt</Button>
          <Button 
            variant="contained" 
            onClick={() => { 
              setConfirmSyncDabas(false); 
              if (onSyncAllDabas) onSyncAllDabas(); 
            }}
            sx={{ backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' } }}
          >
            Synka alla
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
}

export default TopAppBar; 